import React from "react";
import { Box, Card, Typography } from "@mui/material";
import {
  AccountBalanceOutlined,
  WaterDropOutlined,
  ShoppingBagOutlined,
  ShieldOutlined,
  CellTowerOutlined,
  FactoryOutlined,
  HelpOutlineOutlined,
} from "@mui/icons-material";

interface BeneficioIconoProps {
  texto: string;
  icono: string;
  descripcion?: string;
}

export default function BeneficioIcono({ texto, icono, descripcion }: BeneficioIconoProps) {
  // Map icons from json strings to MUI Icons
  const getIcon = (iconName: string) => {
    const cleanName = iconName.toLowerCase();
    const iconProps = { 
      sx: { 
        fontSize: "2.2rem", 
        color: "#00E5FF", 
        transition: "all 0.3s ease", 
        position: "relative", 
        zIndex: 10,
        ".group:hover &": {
          color: "#ffffff",
          transform: "scale(1.1)",
        }
      } 
    };

    if (cleanName.includes("banking")) return <AccountBalanceOutlined {...iconProps} />;
    if (cleanName.includes("oil-gas")) return <WaterDropOutlined {...iconProps} />;
    if (cleanName.includes("finance")) return <ShoppingBagOutlined {...iconProps} />;
    if (cleanName.includes("assurance")) return <ShieldOutlined {...iconProps} />;
    if (cleanName.includes("telecom")) return <CellTowerOutlined {...iconProps} />;
    if (cleanName.includes("supply-chain")) return <FactoryOutlined {...iconProps} />;
    
    return <HelpOutlineOutlined {...iconProps} />;
  };

  return (
    <Card
      className="group"
      sx={{
        bgcolor: "#0A0A0C",
        border: "1px solid rgba(255, 255, 255, 0.04)",
        borderRadius: "2rem",
        p: { xs: 3, md: 4.5 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "flex-start",
        width: "100%",
        height: "100%",
        minHeight: { xs: 208, md: 208 },
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
        cursor: "default",
        "&:hover": {
          borderColor: "rgba(0, 229, 255, 0.4)",
          boxShadow: "0 0 35px rgba(0,229,255,0.18)",
          transform: "scale(1.02)",
        },
      }}
    >
      {/* Dynamic Glow Spotlight Inside Card on hover */}
      <Box
        className="glow-spotlight"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(to bottom right, rgba(0, 229, 255, 0.08), transparent)",
          opacity: 0,
          transition: "opacity 0.5s ease-out",
          pointerEvents: "none",
          zIndex: 0,
          ".group:hover &": {
            opacity: 1,
          }
        }}
      />

      {/* Top Row with elegant floating circular icon */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "relative", zIndex: 10 }}>
        <Box
          sx={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            background: "linear-gradient(to bottom right, #0f172a, #020617, #002766)",
            border: "1px solid rgba(255, 255, 255, 0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            boxShadow: "inset 0 2px 4px rgba(0,0,0,0.6)",
            overflow: "hidden",
          }}
          aria-label={descripcion || ""}
        >
          {/* Glowing highlight sphere inside/behind */}
          <Box
            className="icon-inner-glow"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: "50%",
              backgroundColor: "rgba(0, 229, 255, 0.04)",
              filter: "blur(6px)",
              transition: "all 0.3s",
              ".group:hover &": {
                backgroundColor: "rgba(0, 229, 255, 0.1)",
              }
            }}
          />
          {getIcon(icono)}
        </Box>
      </Box>

      {/* Industry Title strictly styled in Montserrat SemiBold */}
      <Box sx={{ position: "relative", zIndex: 10, mt: "auto", pt: 4 }}>
        <Typography
          variant="h3"
          className="card-title"
          sx={{
            color: "#ffffff",
            fontWeight: 600,
            fontSize: "1.25rem",
            letterSpacing: "0.025em",
            transition: "color 0.3s",
            ".group:hover &": {
              color: "#00E5FF",
            }
          }}
        >
          {texto}
        </Typography>
      </Box>

      {/* Discrete premium corner frame elements that illuminate on active focus */}
      <Box
        className="corner-tr"
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "32px",
          height: "32px",
          borderTop: "1px solid rgba(255, 255, 255, 0.02)",
          borderRight: "1px solid rgba(255, 255, 255, 0.02)",
          borderTopRightRadius: "2rem",
          transform: "translate(4px, -4px)",
          transition: "all 0.3s",
          ".group:hover &": {
            borderColor: "rgba(0, 229, 255, 0.3)",
            transform: "translate(0, 0)",
          }
        }}
      />
      <Box
        className="corner-bl"
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "32px",
          height: "32px",
          borderBottom: "1px solid rgba(255, 255, 255, 0.02)",
          borderLeft: "1px solid rgba(255, 255, 255, 0.02)",
          borderBottomLeftRadius: "2rem",
          transform: "translate(-4px, 4px)",
          transition: "all 0.3s",
          ".group:hover &": {
            borderColor: "rgba(0, 229, 255, 0.3)",
            transform: "translate(0, 0)",
          }
        }}
      />
    </Card>
  );
}
